import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useThemeStore } from '../store/themeStore';
import { themes } from '../config/themes';
import { Palette, Check, Star } from 'lucide-react';
import { usePreferences } from '../hooks/usePreferences';
import './ThemeSelector.css';

const ThemeSelector: React.FC = () => {
  const { currentTheme, setTheme } = useThemeStore();
  const { preferences, updatePreference } = usePreferences();
  const [isOpen, setIsOpen] = useState(false);
  const activeThemeRef = useRef<HTMLDivElement>(null);

  const starredThemes = Array.isArray(preferences.starredThemes) ? preferences.starredThemes : [];

  // Sort themes: starred first, then alphabetically
  const sortedThemes = useMemo(() => {
    const allThemes = Object.values(themes);
    const starred = allThemes.filter(t => starredThemes.includes(t.id));
    const unstarred = allThemes.filter(t => !starredThemes.includes(t.id));
    return [...starred, ...unstarred];
  }, [starredThemes]);

  useEffect(() => {
    if (isOpen && activeThemeRef.current) {
      activeThemeRef.current.scrollIntoView({ block: 'center', behavior: 'instant' });
    }
  }, [isOpen]);

  const handleThemeSelect = (themeId: string) => {
    setTheme(themeId);
    setIsOpen(false);
  };

  const handleToggleStar = (e: React.MouseEvent, themeId: string) => {
    e.stopPropagation(); // Prevent theme selection

    const newStarred = starredThemes.includes(themeId)
      ? starredThemes.filter(id => id !== themeId)
      : [...starredThemes, themeId];

    updatePreference('starredThemes', newStarred);
  };

  return (
    <div className="theme-selector">
      <button
        className="theme-selector-button action-bar-btn"
        onClick={() => setIsOpen(!isOpen)}
        title="Change Theme"
      >
        <Palette size={14} className="action-bar-icon" />
        <span className="action-bar-label">Theme</span>
      </button>

      {isOpen && createPortal(
        <>
          <div className="theme-selector-overlay" onClick={() => setIsOpen(false)} />
          <div className="theme-selector-dropdown">
            <div className="theme-selector-header">
              <h3>Select Theme</h3>
              <button className="theme-selector-close" onClick={() => setIsOpen(false)}>
                ×
              </button>
            </div>

            {/* Show starred section if any starred */}
            {starredThemes.length > 0 && (
              <div className="theme-section-label">
                <Star size={12} className="starred" /> Starred
              </div>
            )}

            <div className="theme-selector-list">
              {sortedThemes.map((theme, index) => {
                const isStarred = starredThemes.includes(theme.id);
                const isFirstUnstarred = index === starredThemes.length && starredThemes.length > 0;

                return (
                  <React.Fragment key={theme.id}>
                    {isFirstUnstarred && (
                      <div className="theme-section-label">All Themes</div>
                    )}
                    <div
                      ref={currentTheme.id === theme.id ? activeThemeRef : null}
                      className={`theme-selector-item ${currentTheme.id === theme.id ? 'active' : ''}`}
                      onClick={() => handleThemeSelect(theme.id)}
                    >
                      <span
                        className={`theme-star-btn ${isStarred ? 'starred' : ''}`}
                        onClick={(e) => handleToggleStar(e, theme.id)}
                        title={isStarred ? 'Remove from starred' : 'Add to starred'}
                        role="button"
                        tabIndex={0}
                      >
                        <Star size={14} fill={isStarred ? 'currentColor' : 'none'} />
                      </span>
                      <div className="theme-preview">
                        <div
                          className="theme-preview-color"
                          style={{ backgroundColor: theme.colors.accent }}
                        />
                        <div
                          className="theme-preview-color"
                          style={{ backgroundColor: theme.colors.success }}
                        />
                        <div
                          className="theme-preview-color"
                          style={{ backgroundColor: theme.colors.warning }}
                        />
                      </div>
                      <div className="theme-info">
                        <div className="theme-name">{theme.name}</div>
                        <div className="theme-description">{theme.description}</div>
                      </div>
                      {currentTheme.id === theme.id && (
                        <div className="theme-checkmark"><Check size={16} /></div>
                      )}
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default ThemeSelector;
